/**
 * F1 News Sources Configuration
 * --------------------------------------------------------------------------
 * Tier 1: Official / Primary sources (highest authority)
 *   - Formula1.com Official
 *   - BBC Sport F1
 * Tier 2: Major established motorsport journalism
 *   - Motorsport.com F1, Autosport F1, The Race F1, RaceFans
 * All feeds are public RSS/Atom. Fetched via rss2json.com proxy to avoid CORS.
 * Free tier (no API key): default 10 items/feed, NO custom `count` param allowed
 * (rss2json returns HTTP 422 "requires api key" if `count` is sent at all,
 * except when it happens to equal the default of 10). Do not add a `count`
 * param here unless RSS2JSON_API_KEY is configured.
 * Free tier rate limit: ~10,000 requests/day shared per IP (unauthenticated).
 * Recommendation: add a simple serverless proxy for production to avoid
 * relying on a third-party free tier.
 */

export const NEWS_SOURCES = [
  {
    id: 'formula1-official',
    name: 'Formula1.com Official',
    url: 'https://www.formula1.com/en/latest/all.xml',
    category: 'race',
    accentColor: '#E10600',
    priority: 1,
    enabled: true,
  },
  {
    id: 'bbc-f1',
    name: 'BBC Sport F1',
    url: 'https://feeds.bbci.co.uk/sport/formula1/rss.xml',
    category: 'feature',
    accentColor: '#BB1919',
    priority: 2,
    enabled: true,
  },
  {
    id: 'motorsport-com',
    name: 'Motorsport.com F1',
    url: 'https://www.motorsport.com/rss/f1/news/',
    category: 'race',
    accentColor: '#FF6B00',
    priority: 3,
    enabled: true,
  },
  {
    id: 'autosport',
    name: 'Autosport F1',
    url: 'https://www.autosport.com/rss/feed/f1',
    category: 'technical',
    accentColor: '#0047AB',
    priority: 4,
    enabled: true,
  },
  {
    id: 'the-race',
    name: 'The Race F1',
    url: 'https://www.the-race.com/category/formula-1/rss/',
    category: 'feature',
    accentColor: '#1A1A2E',
    priority: 5,
    enabled: true,
  },
  {
    id: 'racefans',
    name: 'RaceFans',
    url: 'https://www.racefans.net/feed/',
    category: 'feature',
    accentColor: '#F5C518',
    priority: 6,
    enabled: true,
  },
];

/**
 * ARTICLE_HOST_ALLOWLIST — the only hosts the /api/article reader proxy will
 * fetch from. Matched as "exact domain or subdomain of".
 *
 * This list is the entire security boundary of that endpoint: without it the
 * dev server is an open forwarding proxy that any page in the browser could
 * point at an internal address. Article hostnames are NOT always the feed
 * hostname — BBC syndicates from feeds.bbci.co.uk but the articles live on
 * bbc.co.uk / bbc.com — so this is maintained separately from NEWS_SOURCES.
 */
export const ARTICLE_HOST_ALLOWLIST = [
  'formula1.com',
  'bbc.co.uk',
  'bbc.com',
  'bbci.co.uk',
  'motorsport.com',
  'autosport.com',
  'the-race.com',
  'racefans.net',
];

/** True when `url` is an article page we are willing to fetch. */
export function isAllowedArticleUrl(url) {
  let parsed;
  try {
    parsed = new URL(String(url));
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const host = parsed.hostname.toLowerCase();
  return ARTICLE_HOST_ALLOWLIST.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

export function getEnabledSources() {
  return NEWS_SOURCES
    .filter((s) => s.enabled)
    .sort((a, b) => a.priority - b.priority);
}

export function getSourcesByCategory(category) {
  if (!category || category === 'all') return getEnabledSources();
  if (category === 'breaking') {
    return getEnabledSources().filter((s) => s.category === 'breaking');
  }
  return getEnabledSources().filter((s) => s.category === category);
}

export const FETCH_CONFIG = {
  defaultTtlMs: 1000 * 60 * 10, // 10 minutes
  maxAgeMs: 1000 * 60 * 60, // 1 hour hard max
  concurrency: 3,
  timeoutMs: 10000,
  rss2json: {
    baseUrl: 'https://api.rss2json.com/v1/api.json',
    // Leave `count`/`api_key` unset on the free tier — rss2json rejects any
    // explicit `count` value without a paid API key. Set an env-driven
    // api_key here (and a count) once one is provisioned.
    params: {},
  },
};

export const NEWS_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'breaking', label: 'Breaking' },
  { id: 'race', label: 'Race Weekend' },
  { id: 'technical', label: 'Technical' },
  { id: 'standings', label: 'Standings' },
  { id: 'feature', label: 'Features' },
];

/**
 * CATEGORY_KEYWORDS — used by inferCategory() in utils/newsParser.js.
 *
 * `breaking` is deliberately NOT a category: the UI's Breaking chip filters on
 * the `isBreaking` flag, so anything *categorised* as breaking became
 * unreachable from every chip. It stays here purely to feed detectBreaking().
 *
 * The remaining lists were previously so generic that categorisation collapsed
 * — a 60-article sample scored 42 "race" and only 3 "feature", leaving the
 * Features chip effectively empty. Tokens that match almost any F1 headline
 * ('race', 'win', 'gp ', 'grid', 'position', 'gap', 'inside') have been removed;
 * scoring in newsParser weights multi-word phrases above bare words.
 */
export const CATEGORY_KEYWORDS = {
  breaking: ['breaking', 'just in', 'urgent', 'developing', 'official:'],
  race: [
    'grand prix', 'qualifying', 'sprint race', 'sprint shootout', 'free practice',
    'fp1', 'fp2', 'fp3', 'pole position', 'podium', 'race report', 'race result',
    'starting grid', 'safety car', 'red flag', 'fastest lap', 'pit stop',
    'race winner', 'chequered flag', 'grid penalty', 'stewards',
  ],
  technical: [
    'technical', 'aerodynamic', 'power unit', 'gearbox', 'suspension',
    'tyre', 'tire', 'compound', 'pirelli', 'drs', 'ers', 'mgu-k', 'mgu-h',
    'regulation', 'upgrade', 'floor', 'front wing', 'rear wing', 'sidepod',
    'chassis', 'downforce', 'ride height', 'porpoising', 'wind tunnel',
  ],
  standings: [
    'standings', 'championship', 'championship lead', 'title fight', 'title race',
    'constructors championship', 'drivers championship', 'points table',
    'title contender', 'championship battle',
  ],
  feature: [
    'interview', 'exclusive', 'analysis', 'opinion', 'column', 'feature', 'profile',
    'behind the scenes', 'paddock', 'documentary', 'podcast', 'lifestyle',
    'fashion', 'helmet', 'livery', 'history', 'legend', 'retrospective',
    'explained', 'in numbers', 'verdict', 'what we learned', 'talking points',
  ],
};

export const TEAM_COLORS = {
  ferrari: '#DC0000',
  mercedes: '#00D2BE',
  redbull: '#1E41FF',
  mclaren: '#FF8700',
  astonmartin: '#006F62',
  alpine: '#0090FF',
  williams: '#005AFF',
  rb: '#6692FF',
  sauber: '#52E252',
  haas: '#B6BABD',
  audi: '#ED1C24',
  cadillac: '#FFFFFF',
};